/**
 * Müşteri kazanımı: kaynak, kampanya, temas ve kaynak kesinliği (v3.1 Dalga 2A dilim S9 — D1′ ve D4′). Tasarım:
 * docs/planning/v3/lead-dagitim-ve-kaynak-izleme.md § 3 ve § 6; karar ADR-0016 § 6–7; değerlendirme § 3.1–3.2.
 *
 * - İkinci bir kaynak alanı YOKTUR: aday kaynağı `leads.source_key` (çalışma alanının `source` sözlüğü) olarak kalır. Yeni olan,
 *   kaynağın altındaki KAMPANYA hiyerarşisi (kampanya → grup → reklam) ve her başvurunun TEMAS kaydıdır.
 * - Her temas kaynağın ne kadar KESİN bilindiğini taşır (`platform | click_id | associated | self_reported | unknown`). Seviye
 *   kanıtsız yükseltilmez; bilinmeyen kaynak tahminle bir kampanyaya bağlanmaz.
 * - İlk temas bir kez yazılır (veritabanı tetikleyicisi korur); son temas her yeni temasla güncellenir. Dönüşümde ikisi kişiye ve
 *   fırsata kopyalanır; fırsattaki değerler dönüşüm anının görüntüsüdür.
 * - Tıklama kimlikleri (gclid, fbclid …) YALNIZ çalışma alanı tercihi açıkken ve yalnız şifreli saklanır; tercih varsayılan
 *   kapalıdır ve açmak için yöneticinin aydınlatma/onay metni beyanı gerekir (Castinflow hukuki metin üretmez).
 */
import { z } from 'zod';
import { COST_METRIC_GAPS } from './ad-spend.js';
import { LEAD_STATUSES } from './leads.js';
import { filterAstSchema } from './list-query.js';
import { SOURCE_STATES } from './source-state.js';
const uuid = z.guid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'tarih YYYY-AA-GG olmalı');
// ---------------------------------------------------------------------------------------------------------------------
// Kaynak kesinliği
// ---------------------------------------------------------------------------------------------------------------------
// Kesinlik seviyeleri ayrı modülde (liste kataloğu `list-query.ts` bunu kullanır; bu dosya rapor satırının süzgeç şeması için
// `list-query.ts`'i içe aktarır — modül döngüsü kurulmasın)
export { ACQUISITION_CERTAINTIES } from './acquisition-certainty.js';
import { ACQUISITION_CERTAINTIES } from './acquisition-certainty.js';
export const ACQUISITION_CERTAINTY_LABELS = {
    platform: 'Platform bildirdi',
    click_id: 'Tıklama kimliğiyle çözüldü',
    associated: 'İlişkilendirildi',
    self_reported: 'Beyan',
    unknown: 'Bilinmiyor',
};
export const ACQUISITION_CERTAINTY_HINTS = {
    platform: 'Reklam platformu kampanyayı aynı olayda doğrudan bildirdi.',
    click_id: 'Tıklama kimliği yakalandı ve reklam platformunda kampanyaya çözüldü.',
    associated: 'Bizim kurduğumuz bağ (bağlantı etiketi, referans kodu, eşleme). Kampanya kesin değildir.',
    self_reported: 'Kişinin ya da temsilcinin beyanı. Kampanya kesin değildir.',
    unknown: 'Kaynağa dair kanıt yok.',
};
/** Kampanya bilgisi KESİN sayılan seviyeler; diğerlerinde rapor "kampanya kesin değil" diye işaretler */
export const CERTAIN_CAMPAIGN_CERTAINTIES = [
    'platform',
    'click_id',
];
export function isCampaignCertain(c) {
    return CERTAIN_CAMPAIGN_CERTAINTIES.includes(c);
}
// ---------------------------------------------------------------------------------------------------------------------
// Kampanya hiyerarşisi
// ---------------------------------------------------------------------------------------------------------------------
export const ACQUISITION_PLATFORMS = ['meta', 'google', 'manual', 'other'];
export const ACQUISITION_PLATFORM_LABELS = {
    meta: 'Sosyal medya reklamları',
    google: 'Arama ve görüntülü reklamlar',
    manual: 'Elle tanımlı',
    other: 'Diğer platform',
};
export const ACQUISITION_LEVELS = ['campaign', 'group', 'ad'];
export const ACQUISITION_LEVEL_LABELS = {
    campaign: 'Kampanya',
    group: 'Reklam grubu',
    ad: 'Reklam',
};
/** Üst düzey: grup kampanyanın, reklam grubun altındadır */
export const ACQUISITION_PARENT_LEVEL = { campaign: null, group: 'campaign', ad: 'group' };
export const ACQUISITION_CAMPAIGN_STATUSES = ['active', 'archived'];
/** Dış kimlik (platform kampanya/grup/reklam kimliği) */
export const ACQUISITION_EXTERNAL_ID_RE = /^[A-Za-z0-9._:-]{1,128}$/;
export const acquisitionCampaignSchema = z.object({
    id: uuid,
    platform: z.enum(ACQUISITION_PLATFORMS),
    level: z.enum(ACQUISITION_LEVELS),
    parentId: uuid.nullable(),
    rootId: uuid,
    externalId: z.string().nullable(),
    name: z.string(),
    status: z.enum(ACQUISITION_CAMPAIGN_STATUSES),
    /** Kampanyaya özel WhatsApp referans kodu (yalnız kampanya düzeyinde) */
    waRefCode: z.string().nullable(),
    createdAt: z.string(),
});
export const acquisitionCampaignListSchema = z.object({
    data: z.array(acquisitionCampaignSchema),
});
const campaignName = z.string().trim().min(1, 'ad gerekli').max(200);
export const createAcquisitionCampaignInputSchema = z
    .object({
    platform: z.enum(ACQUISITION_PLATFORMS),
    level: z.enum(ACQUISITION_LEVELS).default('campaign'),
    parentId: uuid.nullable().optional(),
    externalId: z
        .string()
        .trim()
        .regex(ACQUISITION_EXTERNAL_ID_RE, 'geçersiz dış kimlik')
        .nullable()
        .optional(),
    name: campaignName,
})
    .strict();
export const updateAcquisitionCampaignInputSchema = z
    .object({
    name: campaignName.optional(),
    status: z.enum(ACQUISITION_CAMPAIGN_STATUSES).optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Değişiklik yok' });
// ---------------------------------------------------------------------------------------------------------------------
// Normalizasyon ve eşleme
// ---------------------------------------------------------------------------------------------------------------------
export const ACQUISITION_VALUE_MAX = 200;
export const ACQUISITION_RAW_MAX = 500;
/**
 * Normalize değer: Unicode NFKC + kırpma + `tr-TR` küçük harf + noktasız ı → i katlaması + boşluk tekleme. Boş ya da yalnız
 * boşluk → null. Katlama, İngilizce büyük "I" ile yazılmış değerin (`Instagram` → tr-TR `ınstagram`) küçük harfli yazımla
 * (`instagram`) aynı anahtara inmesi içindir; ham değer ayrıca saklanır. Eşleme tablosu normalize değer üzerinden çalışır.
 */
export function normalizeAcquisitionValue(raw) {
    if (raw === null || raw === undefined)
        return null;
    const s = raw
        .normalize('NFKC')
        .trim()
        .toLocaleLowerCase('tr-TR')
        .replace(/ı/g, 'i')
        .replace(/\s+/g, ' ');
    if (s === '')
        return null;
    return s.slice(0, ACQUISITION_VALUE_MAX);
}
export const ACQUISITION_ALIAS_FIELDS = ['source', 'campaign', 'utm_source', 'utm_medium'];
export const ACQUISITION_ALIAS_FIELD_LABELS = {
    source: 'Kaynak (bağlantı etiketindeki kaynak → aday kaynağı)',
    campaign: 'Kampanya (bağlantı etiketindeki kampanya → kampanya)',
    utm_source: 'Bağlantı etiketi: kaynak (yazım birleştirme)',
    utm_medium: 'Bağlantı etiketi: ortam (yazım birleştirme)',
};
export const acquisitionAliasSchema = z.object({
    id: uuid,
    field: z.enum(ACQUISITION_ALIAS_FIELDS),
    rawNormalized: z.string(),
    target: z.string(),
    /** Hedefin görünen adı (kaynak etiketi ya da kampanya adı; bulunamazsa null) */
    targetLabel: z.string().nullable(),
    createdAt: z.string(),
});
export const acquisitionAliasListSchema = z.object({ data: z.array(acquisitionAliasSchema) });
export const createAcquisitionAliasInputSchema = z
    .object({
    field: z.enum(ACQUISITION_ALIAS_FIELDS),
    raw: z.string().trim().min(1, 'değer gerekli').max(ACQUISITION_RAW_MAX),
    target: z.string().trim().min(1, 'hedef gerekli').max(ACQUISITION_VALUE_MAX),
})
    .strict();
// ---------------------------------------------------------------------------------------------------------------------
// Açılış yolu ve yönlendiren
// ---------------------------------------------------------------------------------------------------------------------
/** Açılış yolu: yalnız yol bölümü (sorgu dizesi ve parça atılır — tıklama kimliği ve kişisel veri taşıyabilir). */
export function sanitizeLandingPath(raw) {
    if (raw === null || raw === undefined)
        return null;
    const s = raw.trim();
    if (s === '')
        return null;
    let path;
    try {
        path = new URL(s, 'https://yer.tutucu.invalid').pathname;
    }
    catch {
        return null;
    }
    return path.slice(0, 500) || '/';
}
/** Yönlendiren: yalnız alan adı (küçük harf). Geçersiz adres → null. */
export function referrerHostOf(raw) {
    if (raw === null || raw === undefined || raw.trim() === '')
        return null;
    try {
        const u = new URL(raw.trim());
        if (u.protocol !== 'http:' && u.protocol !== 'https:')
            return null;
        return u.hostname.toLowerCase().slice(0, 253) || null;
    }
    catch {
        return null;
    }
}
// ---------------------------------------------------------------------------------------------------------------------
// Tıklama kimlikleri
// ---------------------------------------------------------------------------------------------------------------------
export const CLICK_ID_KINDS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid'];
export const CLICK_ID_MAX = 500;
/**
 * Temasta saklanabilen tıklama kimliği türleri: web/form kaynaklı türler + tıkla-sohbet reklamının tıklama kimliği
 * (`ctwa_clid`; v3.1 S14 — yalnız iş mesajlaşma hattının ilk iletisinden, tercih açıkken). Gelen aday yüklerinin sözleşmesi
 * `CLICK_ID_KINDS` ile değişmeden kalır.
 */
export const TOUCH_CLICK_ID_KINDS = [...CLICK_ID_KINDS, 'ctwa_clid'];
// ---------------------------------------------------------------------------------------------------------------------
// WhatsApp referans kodu (kampanyaya özel önceden doldurulmuş metin kodu)
// ---------------------------------------------------------------------------------------------------------------------
/**
 * Kod biçimi `KMP-XXXXXX`: karıştırılabilecek karakterler (0/O, 1/I/L) yoktur. Kod kampanya düzeyindedir ve çalışma alanında
 * tekildir. Gelen ilk iletide kod bulunursa temas `associated` (kullanıcı metni silebilir — olasılıksal); bulunmazsa `unknown`.
 */
export const WA_REF_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const WA_REF_CODE_RE = /^KMP-[23456789ABCDEFGHJKMNPQRSTUVWXYZ]{6}$/;
const WA_REF_CODE_SCAN = /(?:^|[^A-Za-z0-9])(KMP-[23456789ABCDEFGHJKMNPQRSTUVWXYZ]{6})(?![A-Za-z0-9])/i;
/** Metindeki referans kodu (büyük/küçük harf duyarsız; ilk eşleşme) ya da null */
export function extractWaRefCode(text) {
    if (text === null || text === undefined)
        return null;
    const m = WA_REF_CODE_SCAN.exec(text.normalize('NFKC'));
    return m?.[1] === undefined ? null : m[1].toUpperCase();
}
/** Rastgele bayt dizisinden kod (çağıran güvenli rastgele kaynak verir) */
export function waRefCodeFromBytes(bytes) {
    let out = 'KMP-';
    for (let i = 0; i < 6; i += 1)
        out += WA_REF_CODE_ALPHABET[(bytes[i] ?? 0) % WA_REF_CODE_ALPHABET.length] ?? '2';
    return out;
}
export const WA_PHONE_RE = /^\+[1-9][0-9]{6,14}$/;
export const WA_DEFAULT_TEXT = 'Merhaba, bilgi almak istiyorum.';
/** wa.me bağlantısı: numara (E.164) + önceden doldurulmuş metin (kod sona eklenir) */
export function waMeLink(phoneE164, code, text = WA_DEFAULT_TEXT) {
    const digits = phoneE164.replace(/[^0-9]/g, '');
    return `https://wa.me/${digits}?text=${encodeURIComponent(`${text} (Kod: ${code})`)}`;
}
// ---------------------------------------------------------------------------------------------------------------------
// Temas
// ---------------------------------------------------------------------------------------------------------------------
export const ACQUISITION_CHANNELS = [
    'webhook',
    'google_lead_form',
    'web_form',
    'whatsapp',
    'manual',
    /** Temassız eski adayın dönüşümünde oluşturulan temas (kaynak adayın kaynağı, kesinlik `unknown`) */
    'other',
    /** v3.1 S13: sosyal medya sayfası potansiyel müşteri formu (sağlayıcı bağlantısı) */
    'meta_lead_ads',
];
export const ACQUISITION_CHANNEL_LABELS = {
    webhook: 'Genel web kancası',
    google_lead_form: 'Reklam potansiyel müşteri formu',
    web_form: 'Web formu',
    whatsapp: 'WhatsApp iletisi',
    manual: 'Elle giriş',
    other: 'Önceki kayıt (temas bilgisi yok)',
    meta_lead_ads: 'Sosyal medya potansiyel müşteri formu',
};
export const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'];
export const UTM_LABELS = {
    source: 'Kaynak',
    medium: 'Ortam',
    campaign: 'Kampanya',
    term: 'Anahtar kelime',
    content: 'İçerik',
};
export const campaignPathItemSchema = z.object({
    id: uuid,
    level: z.enum(ACQUISITION_LEVELS),
    name: z.string(),
    platform: z.enum(ACQUISITION_PLATFORMS),
});
export const acquisitionTouchViewSchema = z.object({
    id: uuid,
    occurredAt: z.string(),
    channel: z.enum(ACQUISITION_CHANNELS),
    sourceKey: z.string(),
    certainty: z.enum(ACQUISITION_CERTAINTIES),
    /** Kampanya hiyerarşisi (kampanya → grup → reklam); bağlı kampanya yoksa boş */
    campaignPath: z.array(campaignPathItemSchema),
    /** Normalize bağlantı etiketleri (ham değerler gösterilmez) */
    utm: z.object({
        source: z.string().nullable(),
        medium: z.string().nullable(),
        campaign: z.string().nullable(),
        term: z.string().nullable(),
        content: z.string().nullable(),
    }),
    landingPath: z.string().nullable(),
    referrerHost: z.string().nullable(),
    /** Saklanan tıklama kimliklerinin TÜRLERİ (değerler hiçbir yanıtta yoktur) */
    clickIdKinds: z.array(z.enum(TOUCH_CLICK_ID_KINDS)),
    refCode: z.string().nullable(),
    leadEventId: uuid.nullable(),
});
/** Kayıt sayfası "Kaynak" paneli (aday, kişi, fırsat) */
export const recordAcquisitionSchema = z.object({
    recordType: z.enum(['lead', 'contact', 'opportunity']),
    firstTouch: acquisitionTouchViewSchema.nullable(),
    lastTouch: acquisitionTouchViewSchema.nullable(),
    /** Adayın bütün temasları (yeniden başvurular dahil; en yeni önce). Kişi/fırsatta boş. */
    touches: z.array(acquisitionTouchViewSchema),
    /** Aday: eşleştirme kuyruğunda bekleyen olası mükerrer çift sayısı */
    possibleDuplicates: z.number().int(),
    /** Fırsat/kişi: değerler dönüşüm anının görüntüsüdür */
    snapshot: z.boolean(),
});
// ---------------------------------------------------------------------------------------------------------------------
// Çalışma alanı tercihleri
// ---------------------------------------------------------------------------------------------------------------------
export const LEAD_REAPPLY_POLICIES = ['merge_into_existing', 'create_flagged'];
export const LEAD_REAPPLY_POLICY_LABELS = {
    merge_into_existing: 'Mevcut açık adaya temas olarak ekle ve sorumluya "tekrar başvurdu" bildirimi gönder',
    create_flagged: 'Yeni aday oluştur, "olası mükerrer" olarak eşleştirme kuyruğuna ekle',
};
export const acquisitionSettingsSchema = z.object({
    clickIdCollection: z.boolean(),
    /** Yöneticinin "aydınlatma/onay metni onaylandı" beyanı (tarih + beyan eden); tercih açıksa doludur */
    clickIdNoticeConfirmedAt: z.string().nullable(),
    clickIdNoticeConfirmedBy: z.string().nullable(),
    duplicatePolicy: z.enum(LEAD_REAPPLY_POLICIES),
    qualifiedStatuses: z.array(z.enum(LEAD_STATUSES)),
    whatsappNumber: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
export const updateAcquisitionSettingsInputSchema = z
    .object({
    clickIdCollection: z.boolean().optional(),
    /** Tercihi AÇMAK için zorunlu: yönetici aydınlatma/onay metninin onaylandığını beyan eder */
    noticeConfirmed: z.literal(true).optional(),
    /** Beyanın tarihi (metnin onaylandığı gün; yerel takvim günü) */
    noticeConfirmedOn: isoDate.optional(),
    duplicatePolicy: z.enum(LEAD_REAPPLY_POLICIES).optional(),
    qualifiedStatuses: z.array(z.enum(LEAD_STATUSES)).min(1).max(LEAD_STATUSES.length).optional(),
    whatsappNumber: z
        .string()
        .trim()
        .regex(WA_PHONE_RE, 'numara +905321234567 biçiminde olmalı')
        .nullable()
        .optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Değişiklik yok' })
    .refine((v) => v.clickIdCollection !== true || v.noticeConfirmed === true, {
    message: 'Tıklama kimliği toplamayı açmak için aydınlatma/onay metninin onaylandığını beyan edin',
    path: ['noticeConfirmed'],
});
// ---------------------------------------------------------------------------------------------------------------------
// Kampanya raporu (D4′)
// ---------------------------------------------------------------------------------------------------------------------
export const ATTRIBUTION_MODELS = ['first_touch', 'last_touch'];
export const ATTRIBUTION_MODEL_LABELS = {
    first_touch: 'İlk temas',
    last_touch: 'Son temas',
};
/** Rapor penceresi üst sınırı (gün) */
export const CAMPAIGN_REPORT_MAX_DAYS = 400;
export const campaignReportQuerySchema = z
    .object({
    from: isoDate,
    to: isoDate,
    model: z.enum(ATTRIBUTION_MODELS).default('last_touch'),
    /** Virgülle ayrılmış kesinlik seviyeleri; boş = hepsi */
    certainty: z
        .string()
        .max(200)
        .optional()
        .transform((s, ctx) => {
        if (s === undefined || s.trim() === '')
            return [...ACQUISITION_CERTAINTIES];
        const parts = [...new Set(s.split(',').map((x) => x.trim()))];
        for (const p of parts)
            if (!ACQUISITION_CERTAINTIES.includes(p)) {
                ctx.addIssue({ code: 'custom', message: 'geçersiz kesinlik seviyesi' });
                return z.NEVER;
            }
        return parts;
    }),
})
    .strict()
    .refine((q) => q.from <= q.to, { message: 'başlangıç bitişten sonra olamaz', path: ['to'] });
export const revenueByCurrencySchema = z.object({ currency: z.string(), amount: z.string() });
/** En alt düzey satır: kaynak × kampanya (en özel düzey) × kesinlik */
export const campaignReportRowSchema = z.object({
    sourceKey: z.string(),
    /**
     * Pazarlama çalışma merkezi (24 Ekim 2026): kaynağın görünen adı SUNUCUDA çözülür (tenant adı → sistem etiketi; sözlükte yoksa
     * "Silinmiş kaynak") — ekran sözlüğü yüklenmeden ya da yüklenemeden ham anahtar (`cold_call`, `c_…`) gösterilmez. Bilinmeyen
     * kaynak Google/Meta/organik diye etiketlenmez. Sunucu her satırda doldurur; isteğe bağlı olması yalnız eski istemci uyumu içindir.
     */
    sourceLabel: z.string().optional(),
    /** `true` etkin, `false` pasif, `null` sözlükte yok */
    sourceActive: z.boolean().nullable().optional(),
    /**
     * 2. tur (§ 5): kaynak çözümleme durumu — `resolved`/`inactive` sözlükte; `deleted` yalnız denetim kaydında silinme olayı
     * varsa; kanıtsız eksik anahtar `not_found`. Ham anahtar (`sourceKey`) teşhis içindir, arayüzde gösterilmez.
     */
    sourceState: z.enum(SOURCE_STATES).optional(),
    /**
     * Pazarlama çalışma merkezi (24 Ekim 2026): satırın ADAY sütununun hedef listesi (aday liste süzgeci): dönem (çalışma alanı
     * saat dilimi) + seçilen modele göre temas kaynağı (`firstTouchSource`/`lastTouchSource`, raporla aynı ifade) + kök kampanya
     * (kampanyasız satırda `is_null`) + kesinlik (bilinmeyen = boş ya da `unknown`). `filterExact: false` → satır alt düzey
     * (reklam grubu/reklam) kampanyadır ve liste kök kampanyanın TÜM adaylarını gösterir (sayıdan geniş olabilir).
     * `qualifiedFilter`: nitelikli sütununun listesi (aynı süzgeç + raporun nitelikli durumları). Sunucu doldurur.
     */
    filter: z.lazy(() => filterAstSchema).optional(),
    filterExact: z.boolean().optional(),
    qualifiedFilter: z.lazy(() => filterAstSchema).optional(),
    /** Kampanya yolu (kampanya → grup → reklam); kampanyasız satırda boş */
    campaignPath: z.array(campaignPathItemSchema),
    certainty: z.enum(ACQUISITION_CERTAINTIES),
    leads: z.number().int(),
    qualified: z.number().int(),
    opportunities: z.number().int(),
    won: z.number().int(),
    /** Kazanılan fırsat cirosu — PARA BİRİMİ BAŞINA ayrı (kur kaynağı olmadan toplanmaz) */
    revenue: z.array(revenueByCurrencySchema),
});
/** Maliyet ölçüsü: değer ya da üretilmeme nedeni */
export const costMetricSchema = z.union([
    z.object({ ok: z.literal(true), value: z.number() }),
    z.object({ ok: z.literal(false), eksik: z.enum(COST_METRIC_GAPS) }),
]);
/** Kök kampanya başına harcama ve maliyet ölçüleri (kampanya raporu) */
export const campaignCostSchema = z.object({
    campaignId: z.string(),
    name: z.string(),
    /** Harcama, para birimi başına ayrı */
    spend: z.array(revenueByCurrencySchema),
    /** Harcama verisi bu aralık için tam mı ve değilse nedenleri */
    complete: z.boolean(),
    reasons: z.array(z.string()),
    /** Reklam getirisi (kazanılan ciro / harcama) */
    roas: costMetricSchema,
    /** Kazanılan müşteri (fırsat) başına maliyet */
    cac: costMetricSchema,
    /**
     * v3.3 DEMO: harcamanın (en az bir satırı) demo çalışma alanının örnek reklam hesabından geliyor — SENTETİK veridir, hiçbir
     * reklam platformundan okunmamıştır; raporda "Demo verisi" olarak etiketlenir.
     */
    demo: z.boolean().optional(),
});
export const campaignReportSchema = z.object({
    from: z.string(),
    to: z.string(),
    timeZone: z.string(),
    model: z.enum(ATTRIBUTION_MODELS),
    certainty: z.array(z.enum(ACQUISITION_CERTAINTIES)),
    qualifiedStatuses: z.array(z.enum(LEAD_STATUSES)),
    rows: z.array(campaignReportRowSchema),
    totals: z.object({
        leads: z.number().int(),
        qualified: z.number().int(),
        opportunities: z.number().int(),
        won: z.number().int(),
        revenue: z.array(revenueByCurrencySchema),
    }),
    /** Kesinlik seviyesine göre aday dağılımı (süzgeçten bağımsız: seçilen pencerenin tamamı) */
    certaintyDistribution: z.array(z.object({ certainty: z.enum(ACQUISITION_CERTAINTIES), leads: z.number().int() })),
    /**
     * Harcama verisi (v3.1 S11 — reklam harcaması salt okuma). Bağlı değilse (`connected: false`) maliyet, ROAS ve edinme maliyeti
     * YOKTUR (boş sütun ya da tahmin gösterilmez). `coverage` = harcaması bilinen kampanyaların aday payı (0–1). `totals`:
     * harcama para birimi başına (kur kaynağı olmadan toplanmaz). `campaigns`: kök kampanya başına harcama ve maliyet ölçüleri —
     * harcama, para birimi ve dönem uyuşmadan değer üretilmez, nedeni (`eksik`) döner.
     */
    spend: z.object({
        connected: z.boolean(),
        coverage: z.number(),
        totals: z.array(revenueByCurrencySchema).default([]),
        campaigns: z.array(campaignCostSchema).default([]),
        /** v3.3 DEMO: harcama verisinin tamamı ya da bir kısmı demo (sentetik) reklam hesabından */
        demo: z.boolean().optional(),
    }),
});
/** Oran (pay / payda); payda 0 → null (gösterimde "—") */
export function ratio(n, d) {
    return d === 0 ? null : n / d;
}
//# sourceMappingURL=acquisition.js.map